/* Offline download manager — uses the Cache API ('qm-audio-v1').
 * Provides ergonomic helpers used by the Library / Music / AI cards and
 * the Profile downloads panel. Returns null on browsers without Cache API.
 */
const AUDIO_CACHE = 'qm-audio-v1';
const META_KEY = 'qm_downloads_meta_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(audioUrl) {
    if (!audioUrl) return '';
    if (audioUrl.startsWith('http://') || audioUrl.startsWith('https://')) return audioUrl;
    if (audioUrl.startsWith('/api/')) return `${BACKEND_URL}${audioUrl}`;
    return audioUrl;
}

function loadMeta() {
    try { return JSON.parse(localStorage.getItem(META_KEY) || '{}'); } catch { return {}; }
}
function saveMeta(m) {
    try { localStorage.setItem(META_KEY, JSON.stringify(m)); } catch {}
}

export function isSupported() {
    return typeof caches !== 'undefined' && typeof window !== 'undefined' && 'storage' in navigator;
}

export async function isDownloaded(audioUrl) {
    if (!isSupported()) return false;
    try {
        const cache = await caches.open(AUDIO_CACHE);
        const res = await cache.match(resolveUrl(audioUrl));
        return !!res;
    } catch { return false; }
}

export async function downloadItem(item) {
    /**
     * item: {id, title, audio_url, category, kind ('meditation'|'music'|'ai_meditation'|'ai_visualization'), cover_image?, duration_minutes?}
     */
    if (!isSupported()) throw new Error('Offline downloads are not supported in this browser.');
    const url = resolveUrl(item.audio_url);
    const cache = await caches.open(AUDIO_CACHE);
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    // Store full response (with body) keyed by the URL.
    await cache.put(url, res.clone());
    const blob = await res.blob();
    const meta = loadMeta();
    meta[url] = {
        id: item.id,
        title: item.title,
        kind: item.kind,
        category: item.category || null,
        cover_image: item.cover_image || null,
        duration_minutes: item.duration_minutes || null,
        size_bytes: blob.size,
        downloaded_at: new Date().toISOString(),
    };
    saveMeta(meta);
    return meta[url];
}

export async function removeItem(audioUrl) {
    if (!isSupported()) return;
    const url = resolveUrl(audioUrl);
    try {
        const cache = await caches.open(AUDIO_CACHE);
        await cache.delete(url);
    } catch {}
    const meta = loadMeta();
    delete meta[url];
    saveMeta(meta);
}

export async function listDownloads() {
    if (!isSupported()) return { items: [], total_bytes: 0 };
    const meta = loadMeta();
    try {
        const cache = await caches.open(AUDIO_CACHE);
        const keys = await cache.keys();
        const present = new Set(keys.map((k) => k.url));
        // Drop meta entries whose cache entry is gone
        let changed = false;
        Object.keys(meta).forEach((k) => {
            if (!present.has(k)) { delete meta[k]; changed = true; }
        });
        if (changed) saveMeta(meta);
    } catch {}
    const items = Object.entries(meta).map(([url, m]) => ({ url, ...m }));
    items.sort((a, b) => (b.downloaded_at || '').localeCompare(a.downloaded_at || ''));
    const total_bytes = items.reduce((s, i) => s + (i.size_bytes || 0), 0);
    return { items, total_bytes };
}

export async function clearAll() {
    if (!isSupported()) return;
    try { await caches.delete(AUDIO_CACHE); } catch {}
    saveMeta({});
}

export function formatBytes(n) {
    if (!n) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB'];
    let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}
